import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useCart } from '@/context/CartContext';
import MorseCodeVisualizer from '@/components/MorseCodeVisualizer';
import { Button } from '@/components/ui/button';

// PayPal configuration
const PAYPAL_EMAIL = 'andy.a.holt@gmail.com';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, cartId, updateQuantity, removeFromCart, clearCart, loading } = useCart();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedCode, setAppliedCode] = useState(null);
  const [appliedPercent, setAppliedPercent] = useState(0);
  const [builderDiscount, setBuilderDiscount] = useState(null);
  const [applyingCode, setApplyingCode] = useState(false);

  // Customer + delivery details — required before PayPal redirect so we can
  // (a) email a real receipt, (b) tell Lindy where to ship, (c) auto-fill
  // the PayPal shipping form so returning customers don't retype anything.
  const [custFirstName, setCustFirstName] = useState('');
  const [custLastName, setCustLastName] = useState('');
  const [custEmail, setCustEmail] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custAddress1, setCustAddress1] = useState('');
  const [custAddress2, setCustAddress2] = useState('');
  const [custCity, setCustCity] = useState('');
  const [custPostalCode, setCustPostalCode] = useState('');
  const [custCountry, setCustCountry] = useState('CY');
  const [custNotes, setCustNotes] = useState('');

  // Hydrate from localStorage so a refresh doesn't lose what they've typed.
  useEffect(() => {
    try {
      const raw = localStorage.getItem('ww-delivery-details');
      if (raw) {
        const d = JSON.parse(raw);
        // Support both new structured fields and the legacy "name / address"
        // shape so returning visitors don't lose their info.
        if (d.firstName || d.lastName) {
          setCustFirstName(d.firstName || '');
          setCustLastName(d.lastName || '');
        } else if (d.name) {
          const parts = String(d.name).trim().split(/\s+/);
          setCustFirstName(parts[0] || '');
          setCustLastName(parts.slice(1).join(' ') || '');
        }
        setCustEmail(d.email || '');
        setCustPhone(d.phone || '');
        setCustAddress1(d.address1 || '');
        setCustAddress2(d.address2 || '');
        setCustCity(d.city || '');
        setCustPostalCode(d.postalCode || '');
        setCustCountry(d.country || 'CY');
        setCustNotes(d.notes || '');
      }
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('ww-delivery-details', JSON.stringify({
        firstName: custFirstName,
        lastName: custLastName,
        email: custEmail,
        phone: custPhone,
        address1: custAddress1,
        address2: custAddress2,
        city: custCity,
        postalCode: custPostalCode,
        country: custCountry,
        notes: custNotes,
      }));
    } catch (_e) { /* ignore */ }
  }, [custFirstName, custLastName, custEmail, custPhone, custAddress1, custAddress2, custCity, custPostalCode, custCountry, custNotes]);

  // Fetch the admin-configurable builder discount so we know its code on this page
  // (used only to decide whether to flag "ww-builder-discount-redeemed" at checkout).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/discounts/builder`);
        if (!cancelled) setBuilderDiscount(data);
      } catch {
        // intentional: fallback to WELCOME10-only flow if the API is unreachable (see /app/CODE_REVIEW_NOTES.md §5)
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pricing math
  const subtotal = cart.total || 0;
  const itemCount = cart.item_count || 0;
  const bundleDiscount = itemCount >= 3 ? subtotal * 0.15 : 0;
  const codeDiscount = appliedCode ? (subtotal - bundleDiscount) * (appliedPercent / 100) : 0;
  const grandTotal = Math.max(0, subtotal - bundleDiscount - codeDiscount);

  // Server-side validation: do NOT trust client-side string comparison.
  // The server is the source of truth for which codes exist and what % they're worth.
  const applyCode = async () => {
    const c = discountCode.trim().toUpperCase();
    if (!c) {
      toast.error('Enter a code first');
      return;
    }
    if (appliedCode === c) {
      toast.info('That code is already applied');
      return;
    }
    try {
      setApplyingCode(true);
      const { data } = await axios.post(`${API}/discounts/validate`, { code: c });
      if (data?.valid) {
        setAppliedCode(data.code);
        setAppliedPercent(data.percent_off || 0);
        setDiscountCode(data.code);
        toast.success(`${data.percent_off}% discount applied!`);
      } else {
        toast.error(data?.reason || 'Invalid code');
      }
    } catch (err) {
      toast.error('Could not verify that code — please try again');
    } finally {
      setApplyingCode(false);
    }
  };

  const removeCode = () => {
    setAppliedCode(null);
    setAppliedPercent(0);
    setDiscountCode('');
  };

  const handlePayPalCheckout = async () => {
    if (!cart.items || cart.items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    // Validate required delivery details
    const firstName = custFirstName.trim();
    const lastName = custLastName.trim();
    const email = custEmail.trim();
    const address1 = custAddress1.trim();
    const city = custCity.trim();
    const postalCode = custPostalCode.trim();
    const country = (custCountry || 'CY').trim().toUpperCase();
    const missing = [];
    if (!firstName) missing.push('first name');
    if (!lastName) missing.push('last name');
    if (!email) missing.push('email');
    if (!address1) missing.push('address');
    if (!city) missing.push('city');
    if (!postalCode) missing.push('postal code');
    if (missing.length) {
      toast.error(`Please add your ${missing.join(', ')} before paying`);
      const el = document.querySelector('[data-testid="delivery-details-section"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    if (!emailOk) {
      toast.error('Please enter a valid email address');
      return;
    }

    const address2 = custAddress2.trim();
    const fullName = `${firstName} ${lastName}`.trim();
    const shippingAddressBlob = [
      address1,
      address2 || null,
      `${postalCode} ${city}`.trim(),
      country,
    ].filter(Boolean).join('\n');

    setCheckoutLoading(true);

    // 1) Create the order server-side so we have an email + address snapshot
    let orderId = null;
    try {
      const { data } = await axios.post(`${API}/orders`, {
        cart_id: cartId,
        customer_name: fullName,
        customer_email: email,
        customer_phone: custPhone.trim() || null,
        shipping_address: shippingAddressBlob,
        shipping_first_name: firstName,
        shipping_last_name: lastName,
        shipping_address_line1: address1,
        shipping_address_line2: address2 || null,
        shipping_city: city,
        shipping_postal_code: postalCode,
        shipping_country: country,
        notes: custNotes.trim() || null,
        discount_code: appliedCode || null,
        discount_percent: appliedPercent || 0,
        bundle_discount_applied: itemCount >= 3,
      });
      orderId = data.order_id;
    } catch (err) {
      setCheckoutLoading(false);
      toast.error('Could not save your order — please try again');
      return;
    }

    // Build item description
    const itemNames = cart.items.map(item =>
      `${item.product?.name}${item.custom_text ? ` (${item.custom_text})` : ''}${item.size_cm ? ` [${item.size_cm}cm]` : ''}${item.clasp ? ` [${item.clasp.replace('-', ' ')}]` : ''} x${item.quantity}`
    ).join(', ');

    // Snapshot purchased items for the success page (used by the "Leave a review" prompt)
    try {
      const snapshot = {
        purchased_at: new Date().toISOString(),
        order_id: orderId,
        items: cart.items
          .filter((i) => i.product?.id)
          .map((i) => ({
            product_id: i.product.id,
            product_name: i.product.name,
            image: i.product.image,
          })),
      };
      localStorage.setItem('ww-last-order', JSON.stringify(snapshot));
    } catch (_e) {
      // Non-blocking — localStorage may be unavailable in private mode (see /app/CODE_REVIEW_NOTES.md §5)
    }

    // If the customer is redeeming the Builder discount code at checkout, stop the popup from re-appearing
    try {
      if (
        builderDiscount &&
        builderDiscount.enabled &&
        appliedCode &&
        appliedCode.toUpperCase() === (builderDiscount.code || '').toUpperCase()
      ) {
        localStorage.setItem('ww-builder-discount-redeemed', '1');
      }
    } catch (_e) {
      // Non-blocking (see /app/CODE_REVIEW_NOTES.md §5)
    }

    // Create PayPal payment URL — include order_id on the return so the
    // success page can call /confirm and trigger the receipt emails.
    // Also pre-fill the customer's shipping details so PayPal shows them
    // the confirmed address on the review screen instead of asking again.
    const paypalUrl = new URL('https://www.paypal.com/cgi-bin/webscr');
    paypalUrl.searchParams.set('cmd', '_xclick');
    paypalUrl.searchParams.set('business', PAYPAL_EMAIL);
    paypalUrl.searchParams.set('amount', grandTotal.toFixed(2));
    paypalUrl.searchParams.set('currency_code', 'EUR');
    paypalUrl.searchParams.set('item_name', `Whispering Wristwear Order: ${itemNames}`.slice(0, 127));
    paypalUrl.searchParams.set('custom', orderId || '');
    paypalUrl.searchParams.set('return', `${window.location.origin}/checkout/success?method=paypal&order_id=${encodeURIComponent(orderId || '')}`);
    paypalUrl.searchParams.set('cancel_return', `${window.location.origin}/cart`);
    // PayPal shipping-address auto-fill (see PayPal _xclick spec).
    // no_shipping=0 → PayPal collects & shows the shipping address.
    // address_override=1 → PayPal uses the values we pass here instead of
    // the buyer's saved address, so the customer sees exactly what they typed.
    paypalUrl.searchParams.set('no_shipping', '0');
    paypalUrl.searchParams.set('address_override', '1');
    paypalUrl.searchParams.set('first_name', firstName);
    paypalUrl.searchParams.set('last_name', lastName);
    paypalUrl.searchParams.set('email', email);
    paypalUrl.searchParams.set('address1', address1);
    if (address2) paypalUrl.searchParams.set('address2', address2);
    paypalUrl.searchParams.set('city', city);
    paypalUrl.searchParams.set('zip', postalCode);
    paypalUrl.searchParams.set('country', country);

    // Clear cart and redirect to PayPal
    clearCart();
    window.location.href = paypalUrl.toString();
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="pt-28 pb-16 min-h-screen" data-testid="empty-cart">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-lg mx-auto text-center">
            <div className="w-20 h-20 bg-[#F5F5F0] rounded-full flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-10 h-10 text-[#84A98C]" strokeWidth={1.5} />
            </div>
            
            <h1 className="heading-display text-3xl text-[#354F52] mb-4">
              Your Cart is Empty
            </h1>
            
            <p className="text-[#52796F] mb-8">
              Looks like you haven&apos;t added any bracelets yet.
              Start shopping to find your perfect piece.
            </p>

            <Link
              to="/products"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="continue-shopping-empty"
            >
              Continue Shopping
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-28 pb-16" data-testid="cart-page">
      <div className="container-custom px-6 md:px-12 lg:px-24">
        <h1 className="heading-display text-3xl md:text-4xl text-[#354F52] mb-8">
          Shopping Cart
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item) => (
              <div
                key={item.product_id}
                className="flex gap-6 p-6 bg-[#F5F5F0] rounded-sm"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Product Image */}
                <Link 
                  to={`/products/${item.product_id}`}
                  className="flex-shrink-0"
                >
                  <img
                    src={item.product?.image}
                    alt={item.product?.name}
                    className="w-24 h-32 object-cover rounded-sm"
                  />
                </Link>

                {/* Product Details */}
                <div className="flex-1 flex flex-col">
                  <Link to={`/products/${item.product_id}`}>
                    <h3 className="font-serif text-lg text-[#354F52] hover:text-[#84A98C] transition-colors">
                      {item.product?.name}
                    </h3>
                  </Link>
                  
                  {/* Mini Morse Preview */}
                  <div className="my-2">
                    <MorseCodeVisualizer text={item.product?.morse_text} size="sm" />
                  </div>

                  {item.custom_text && (
                    <p className="text-sm text-[#84A98C]">
                      Custom: {item.custom_text}
                    </p>
                  )}

                  {item.size_cm && (
                    <p className="text-sm text-[#84A98C]" data-testid={`size-label-${item.product_id}`}>
                      Size: <span>{item.size_cm} cm</span>
                    </p>
                  )}

                  {item.clasp && (
                    <p className="text-sm text-[#84A98C]" data-testid={`clasp-label-${item.product_id}`}>
                      Clasp: <span className="capitalize">{item.clasp.replace('-', ' ')}</span>
                    </p>
                  )}

                  {item.unit_price != null && (
                    <p className="text-xs text-[#84A98C]/80" data-testid={`unit-price-${item.product_id}`}>
                      €{item.unit_price.toFixed(2)} each
                    </p>
                  )}

                  <div className="mt-auto flex items-center justify-between">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        disabled={loading || item.quantity <= 1}
                        className="p-1.5 hover:bg-[#FAF9F6] rounded-full transition-colors disabled:opacity-50"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-4 h-4 text-[#354F52]" strokeWidth={1.5} />
                      </button>
                      <span className="w-8 text-center text-[#354F52]">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        disabled={loading}
                        className="p-1.5 hover:bg-[#FAF9F6] rounded-full transition-colors"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-4 h-4 text-[#354F52]" strokeWidth={1.5} />
                      </button>
                    </div>

                    {/* Price & Remove */}
                    <div className="flex items-center gap-4">
                      <p className="text-[#D4AF37] font-medium">
                        €{item.subtotal?.toFixed(2)}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        disabled={loading}
                        className="p-2 hover:bg-[#FAF9F6] rounded-full transition-colors text-[#BC4749]"
                        data-testid={`remove-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Continue Shopping */}
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-[#84A98C] hover:text-[#354F52] transition-colors"
              data-testid="continue-shopping"
            >
              <ArrowRight className="w-4 h-4 rotate-180" strokeWidth={1.5} />
              <span className="text-sm uppercase tracking-widest">Continue Shopping</span>
            </Link>

            {/* Delivery Details */}
            <div
              className="mt-10 p-6 bg-[#F5F5F0] rounded-sm"
              data-testid="delivery-details-section"
            >
              <div className="flex items-center gap-3 mb-1">
                <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                <h2 className="font-serif text-lg text-[#354F52]">Delivery details</h2>
              </div>
              <p className="text-xs text-[#84A98C] mb-5">
                Required so Lindy can ship your bracelet and send you a receipt.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-first-name">
                    First name *
                  </label>
                  <input
                    id="cust-first-name"
                    type="text"
                    value={custFirstName}
                    onChange={(e) => setCustFirstName(e.target.value)}
                    placeholder="Jane"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-first-name-input"
                    required
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-last-name">
                    Last name *
                  </label>
                  <input
                    id="cust-last-name"
                    type="text"
                    value={custLastName}
                    onChange={(e) => setCustLastName(e.target.value)}
                    placeholder="Smith"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-last-name-input"
                    required
                    autoComplete="family-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-email">
                    Email *
                  </label>
                  <input
                    id="cust-email"
                    type="email"
                    value={custEmail}
                    onChange={(e) => setCustEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-email-input"
                    required
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-phone">
                    Phone (optional)
                  </label>
                  <input
                    id="cust-phone"
                    type="tel"
                    value={custPhone}
                    onChange={(e) => setCustPhone(e.target.value)}
                    placeholder="+357 99 12 34 56"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-phone-input"
                    autoComplete="tel"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-address1">
                    Street address *
                  </label>
                  <input
                    id="cust-address1"
                    type="text"
                    value={custAddress1}
                    onChange={(e) => setCustAddress1(e.target.value)}
                    placeholder="e.g. 12 Odos Aphroditis"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-address1-input"
                    required
                    autoComplete="address-line1"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-address2">
                    Apartment / floor / suite (optional)
                  </label>
                  <input
                    id="cust-address2"
                    type="text"
                    value={custAddress2}
                    onChange={(e) => setCustAddress2(e.target.value)}
                    placeholder="e.g. Flat 3B"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-address2-input"
                    autoComplete="address-line2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-city">
                    City / town *
                  </label>
                  <input
                    id="cust-city"
                    type="text"
                    value={custCity}
                    onChange={(e) => setCustCity(e.target.value)}
                    placeholder="Paphos"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-city-input"
                    required
                    autoComplete="address-level2"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-postal-code">
                    Postal code *
                  </label>
                  <input
                    id="cust-postal-code"
                    type="text"
                    value={custPostalCode}
                    onChange={(e) => setCustPostalCode(e.target.value)}
                    placeholder="8010"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-postal-code-input"
                    required
                    autoComplete="postal-code"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-country">
                    Country *
                  </label>
                  <select
                    id="cust-country"
                    value={custCountry}
                    onChange={(e) => setCustCountry(e.target.value)}
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none"
                    data-testid="cust-country-input"
                    required
                    autoComplete="country"
                  >
                    <option value="CY">Cyprus (free shipping)</option>
                    <option value="GB">United Kingdom</option>
                    <option value="GR">Greece</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                    <option value="IT">Italy</option>
                    <option value="ES">Spain</option>
                    <option value="NL">Netherlands</option>
                    <option value="BE">Belgium</option>
                    <option value="IE">Ireland</option>
                    <option value="PT">Portugal</option>
                    <option value="AT">Austria</option>
                    <option value="SE">Sweden</option>
                    <option value="DK">Denmark</option>
                    <option value="FI">Finland</option>
                    <option value="PL">Poland</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="AU">Australia</option>
                    <option value="NZ">New Zealand</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase tracking-widest text-[#84A98C] mb-1.5" htmlFor="cust-notes">
                    Notes for Lindy (optional)
                  </label>
                  <textarea
                    id="cust-notes"
                    value={custNotes}
                    onChange={(e) => setCustNotes(e.target.value)}
                    rows={2}
                    placeholder="Gift message, preferred bead colour, anything else…"
                    className="w-full px-3 py-2 text-sm bg-[#FAF9F6] text-[#354F52] placeholder-[#CAD2C5] rounded-sm border border-[#CAD2C5] focus:border-[#D4AF37] focus:outline-none resize-y"
                    data-testid="cust-notes-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-[#354F52] p-8 rounded-sm text-[#FAF9F6] sticky top-28">
              <h2 className="font-serif text-xl mb-6">Order Summary</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-[#CAD2C5]">
                  <span>Subtotal ({itemCount} items)</span>
                  <span>€{subtotal.toFixed(2)}</span>
                </div>

                {bundleDiscount > 0 && (
                  <div className="flex justify-between text-[#D4AF37]" data-testid="bundle-discount-line">
                    <span>Bundle discount (15% off, 3+ items)</span>
                    <span>-€{bundleDiscount.toFixed(2)}</span>
                  </div>
                )}

                {itemCount > 0 && itemCount < 3 && (
                  <p className="text-xs text-[#84A98C] bg-[#52796F]/30 p-2.5 rounded" data-testid="bundle-hint">
                    💡 Add {3 - itemCount} more bracelet{3 - itemCount === 1 ? '' : 's'} to unlock a 15% bundle discount
                  </p>
                )}

                {appliedCode && (
                  <div className="flex justify-between text-[#D4AF37]" data-testid="code-discount-line">
                    <span>Code {appliedCode} ({appliedPercent}% off)</span>
                    <span>-€{codeDiscount.toFixed(2)}</span>
                  </div>
                )}

                <div className="flex justify-between text-[#CAD2C5]">
                  <span>Shipping</span>
                  <span className="text-[#D4AF37]">FREE</span>
                </div>
                <p className="text-xs text-[#84A98C]">
                  Free delivery across Cyprus via ACS Couriers
                </p>
              </div>

              {/* Discount code */}
              <div className="mb-6">
                {appliedCode ? (
                  <button
                    onClick={removeCode}
                    className="text-xs text-[#84A98C] hover:text-[#FAF9F6] underline"
                    data-testid="remove-code-btn"
                  >
                    Remove discount code
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value)}
                      placeholder="Discount code"
                      className="flex-1 px-3 py-2 text-sm bg-[#52796F] text-[#FAF9F6] placeholder-[#84A98C] rounded-sm border border-[#52796F] focus:border-[#D4AF37] focus:outline-none"
                      data-testid="discount-code-input"
                    />
                    <button
                      type="button"
                      onClick={applyCode}
                      disabled={applyingCode}
                      className="px-4 py-2 text-xs uppercase tracking-widest bg-[#D4AF37] text-[#354F52] rounded-sm hover:bg-[#D4AF37]/90 transition-colors disabled:opacity-50"
                      data-testid="apply-code-btn"
                    >
                      {applyingCode ? 'Checking…' : 'Apply'}
                    </button>
                  </div>
                )}
              </div>

              <div className="border-t border-[#52796F] pt-4 mb-8">
                <div className="flex justify-between text-lg">
                  <span>Total</span>
                  <span className="text-[#D4AF37] font-medium" data-testid="grand-total">
                    €{grandTotal.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={handlePayPalCheckout}
                disabled={checkoutLoading || loading}
                className="w-full bg-[#0070BA] hover:bg-[#005ea6] text-white py-6 rounded-full font-medium uppercase tracking-widest text-xs flex items-center justify-center gap-2"
                data-testid="checkout-btn"
              >
                {checkoutLoading ? 'Redirecting to PayPal...' : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.641.641 0 0 1 .632-.54h6.012c2.657 0 4.53.625 5.569 1.859.973 1.156 1.245 2.784.808 4.838l-.006.03c-.015.065-.03.13-.047.195-.497 2.143-1.587 3.812-3.24 4.963-1.584 1.103-3.632 1.663-6.089 1.663H7.31l-.234 4.61zm2.717-7.622h.623c1.636 0 2.933-.346 3.858-1.03.937-.693 1.574-1.763 1.893-3.18.156-.678.176-1.263.06-1.74-.116-.476-.35-.862-.695-1.146-.654-.538-1.774-.811-3.33-.811H9.372l-.579 7.907z"/>
                    </svg>
                    Pay with PayPal
                  </>
                )}
              </Button>

              <p className="text-xs text-[#84A98C] text-center mt-4">
                Secure checkout powered by PayPal
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
